import client from './base'

export type ExpenseType = 'cabinet' | 'dossier'
export type ExpenseCategory =
  | 'frais_justice'
  | 'deplacement'
  | 'expertise'
  | 'honoraires_confrere'
  | 'fournitures'
  | 'communication'
  | 'other'
export type ExpenseStatus = 'refacturee' | 'a_refacturer' | 'non_facturable'

export interface Expense {
  id: string
  type: ExpenseType
  category: ExpenseCategory
  description: string
  amount: string
  expense_date: string
  billable: boolean
  status: ExpenseStatus
  case: number | null
  case_title: string | null
  invoice_line: string | null
  assigned_to: { id: string; name: string } | null
  added_by: string
  created_at: string
  updated_at: string
}

export interface ExpenseSummary {
  grand_total: string
  billed_total: string
  by_type: { cabinet?: string; dossier?: string }
  by_category: Record<string, string>
}

export interface ExpenseListParams {
  limit?: number
  offset?: number
  type?: ExpenseType
  category?: ExpenseCategory
  status?: ExpenseStatus
  billable?: boolean
  date_from?: string
  date_to?: string
  case?: number
  ordering?: string
}

export interface CreateExpenseInput {
  type: ExpenseType
  category?: ExpenseCategory
  description: string
  amount: string
  expense_date?: string
  billable?: boolean
  case?: number
  assigned_to?: string
}

export type UpdateExpenseInput = Partial<
  Omit<CreateExpenseInput, 'type' | 'case'>
>

export interface ExpenseListResponse {
  count: number
  next: string | null
  previous: string | null
  results: Expense[]
}

export const expensesService = {
  list: async (params?: ExpenseListParams): Promise<ExpenseListResponse> => {
    const res = await client.get('/api/v1/expenses/', { params })
    return res.data
  },

  summary: async (
    params?: Pick<ExpenseListParams, 'type' | 'date_from' | 'date_to'>,
  ): Promise<ExpenseSummary> => {
    const res = await client.get('/api/v1/expenses/summary/', { params })
    return res.data
  },

  get: async (id: string): Promise<Expense> => {
    const res = await client.get(`/api/v1/expenses/${id}/`)
    return res.data
  },

  create: async (data: CreateExpenseInput): Promise<Expense> => {
    const res = await client.post('/api/v1/expenses/create', data)
    return res.data
  },

  update: async (id: string, data: UpdateExpenseInput): Promise<Expense> => {
    const res = await client.patch(`/api/v1/expenses/${id}/update`, data)
    return res.data
  },

  remove: async (id: string): Promise<void> => {
    await client.delete(`/api/v1/expenses/${id}/remove`)
  },

  listForCase: async (
    caseId: number,
    params?: Pick<ExpenseListParams, 'status' | 'ordering' | 'limit' | 'offset'>,
  ): Promise<ExpenseListResponse> => {
    const res = await client.get(`/api/v1/case/${caseId}/expenses/`, { params })
    return res.data
  },

  addToCase: async (
    caseId: number,
    data: Omit<CreateExpenseInput, 'type' | 'case'>,
  ): Promise<Expense> => {
    const res = await client.post(`/api/v1/case/${caseId}/expense/add`, data)
    return res.data
  },

  bill: async (expenseId: string, invoiceId: string): Promise<Expense> => {
    const res = await client.post(
      `/api/v1/expenses/${expenseId}/bill/${invoiceId}/`,
    )
    return res.data
  },

  unbill: async (expenseId: string): Promise<Expense> => {
    const res = await client.delete(`/api/v1/expenses/${expenseId}/unbill/`)
    return res.data
  },
}
